import { API } from '../../utils';
import {
  IAppealFromRequest,
  IRequest,
  IRequestTemplate,
  IShutdownNotificationParams,
  ISignal,
  SendingChannel,
} from '../../types';
import { IParams } from '../types';
import { ICompletionAct } from '../catalogue/types';
import { GW_SERVICES_PREFIXES } from '../../constants';
import { requestFileFieldType } from 'views/authorized/pages/requests/types';
import { CategoryItemType } from 'store/models/autoParsingStore';

const { C300, REQUEST, AI } = GW_SERVICES_PREFIXES;
type IRequestControlData = {
  text: string;
};

/*Получение списка заявок*/
export const GetRequests = (params: IParams): Promise<(IRequest | ISignal)[]> =>
  API.get(`/${REQUEST}/request/employee/`, undefined, params);

/*Получение рейтинга заявок*/
export const GetRequestRating = (
  params: IParams
): Promise<{ req_id: string; to_int_avg_rating: number }[]> =>
  API.get(`/${REQUEST}/stat/avg_request_rating/`, undefined, params);

/*Получения количества заявок "Принята", "На исполнении", "Просрочена"*/
export const GetRequestsFiltersCount = (params?: IParams) =>
  API.get(`/${REQUEST}/request/employee/summary/`, undefined, params);

/*Получение списка шаблонов заявок*/
export const GetRequestTemplates = (params?: IParams) =>
  API.get(`/${REQUEST}/request/template/`, undefined, params);

/*Получение одной заявки*/
export const GetRequest = (id: string) =>
  API.get(`/${REQUEST}/request/employee/`, id);

/*Получение дерева категорий заявок*/
export const GetCategoriesTree = () =>
  API.get(`/${REQUEST}/constants/request/categories_tree/`);

/*Получение истории изменения заявки*/
export const GetRequestHistory = (id: string) =>
  API.get(`/${REQUEST}/request/employee/history/`, id);

/*Получение статистики по заявкам*/
export const GetTenantRequestStats = (id: string) =>
  API.get(`/${REQUEST}/request/employee/summary/tenant/`, id);

/* Заголовок оптимистичной блокировки: версия карточки, которую видел
 * клиент. Бэкенд (dispatcher) при расхождении с текущей отвечает 409 и не
 * затирает чужую правку. */
const ifMatchHeader = (revision?: number) =>
  revision === undefined || revision === null
    ? undefined
    : { 'If-Match': String(revision) };

/*Изменение заявки*/
export const UpdateRequest = (id: string, data: any, revision?: number) =>
  API.gatewayPatch(
    `/${REQUEST}/request/employee/`,
    id,
    data,
    ifMatchHeader(revision)
  );

/*Изменение статусазаявки*/
export const UpdateRequestStatus = (
  id: string,
  data: any,
  revision?: number
) =>
  API.gatewayPatch(
    `/${REQUEST}/request/employee/`,
    id,
    data,
    ifMatchHeader(revision)
  );

/*Скачивание бланков всех заявок*/
export const GetRequestsBlanks = (params?: IParams) =>
  API.gatewayUnparsedGet(
    `/${REQUEST}/request/employee/blanks/pdf_zip/`,
    undefined,
    params
  );

/** Для резерва файла на сервере */
export const GetReserveRequestFile = (params: {
  filename: string;
}): Promise<{
  shared_id: string;
}> => API.get(`/${REQUEST}/files/reserve/`, undefined, params);

/*Скачивание бланка заявки*/
export const GetBlankByRequestId = (id: string) =>
  API.gatewayUnparsedGet(`/${REQUEST}/request/employee/blanks/pdf/`, id);

/*Выгрузить в Excel (поставить задачу)*/
export const UnloadExcel = (data: IParams) =>
  API.gatewayPost(`/${C300}/report/requests_journal/`, data);

/** Выгрузить в Excel */
export const DownloadRequestsXlsx = (params: IParams) =>
  API.gatewayUnparsedGet(
    `/${REQUEST}/request/employee/blanks/xlsx/`,
    undefined,
    params
  );

/*Выгрузить в Excel (узнать статус)*/
export const GetExcelId = (data: { report_id: string; data: string }) =>
  API.get(`/${C300}/report/requests_journal/`, undefined, data);

/*Удалить заявку*/
export const DeleteRequest = (id: string) =>
  API.delete(`/${REQUEST}/request/employee/`, id);

/*Удалить шаблон заявки*/
export const DeleteRequestTemplate = (id: string) =>
  API.delete(`/${REQUEST}/request/template/`, id);

/*Создать шаблон заявки*/
export const CreateRequestTemplate = (template: IRequestTemplate) =>
  API.gatewayPost(`/${REQUEST}/request/template/`, template);

/*Изменить шаблон заявки*/
export const UpdateRequestTemplate = (template: IRequestTemplate) =>
  API.gatewayPatch(`/${REQUEST}/request/template/`, template._id, template);

/*Создать или скопировать заявку*/
export const CreateRequest = (data: IParams) =>
  API.gatewayPost(`/${REQUEST}/request/employee/`, data);

export const CopyRequest = (requestId: string) =>
  API.gatewayPost(`/${REQUEST}/request/employee/copy/${requestId}/`);

export const GetDuplRequestGroups = (id?: string) =>
  API.get(`/${REQUEST}/request/duplicate_group/`, id);

export const PostDuplRequestGroups = (data: IParams) =>
  API.gatewayPost(`/${REQUEST}/request/duplicate_group/`, data);

export const UpdateDuplRequestGroups = (id: string, params: IParams) =>
  API.gatewayPatch(`/${REQUEST}/request/duplicate_group/`, id, params);

export const DeleteDuplRequestGroups = (id: string) =>
  API.gatewayDelete(`/${REQUEST}/request/duplicate_group/`, id);

/*Добавление файлов к заявке*/
export const SendFiles = (
  id: string,
  data: FormData,
  fileType: requestFileFieldType
) =>
  API.postFilesGtwy(
    `/${REQUEST}/request/employee/${fileType}_files/`,
    id,
    data
  );

/*Провести контроль 2.0 - создание сообщения о контроле*/
export const CreateControlMessage = (
  requestId: string,
  data: IRequestControlData
) =>
  API.gatewayPost(
    `/${REQUEST}/request/employee/control/messages/${requestId}/`,
    data
  );

/*Провести контроль 2.0 - ознакомление с контролем (простановка)*/
export const ReadControlMessage = (requestId: string) =>
  API.gatewayPatch(`/${REQUEST}/request/employee/control/messages/`, requestId);

//TODO: /requests/monitoring/${requestId}/persons_in_charge/ - Patch/Delete - Добавление/удаление ответственных лиц (объекта в список persons_in_charge) контролирующим лицом,
// имеющим право "Редактирование"/"Удаление" в "Контроль журнала заявок" (Удаление в доке не прописано, пока нужно только добавление)

/*Запрос для материалов - документы хранения (КАТАЛОГ)*/
export const GetStorageDocsByRequest = (requestId: string) =>
  API.get(`/${C300}/models/storage_docs_out/`, undefined, {
    request: requestId,
  });

/*Создание документа хранения (КАТАЛОГ)*/
export const CreateStorageDocs = (params: IParams) =>
  API.gatewayPost(`/${C300}/models/storage_docs_out/`, params);

/** operations: array, request: id, warehouse: id **/

/*Обновление документов хранения (КАТАЛОГ)*/
export const UpdateStorageDocs = (docId: string, params: IParams) =>
  API.gatewayPatch(`/${C300}/models/storage_docs_out/`, docId, params);

/** operations: array(position: id, price: number, quantity: string | number), request: id, warehouse: id, id: id **/

/*Запрос для услуг - акт завершения (КАТАЛОГ)*/
export const GetCompletionActsByRequest = (requestId: string) =>
  API.get(`/${C300}/models/completion_acts/`, undefined, {
    request: requestId,
  });

/*Создание акта завершения (КАТАЛОГ)*/
export const CreateCompletionActs = (params: IParams) =>
  API.gatewayPost(`/${C300}/models/completion_acts/`, params);
/** positions: [{service: id, price: number, amount: string | number}] request: id **/

/*Обновление акта завершения (КАТАЛОГ)*/
export const UpdateCompletionActs = (act: ICompletionAct) =>
  API.gatewayPatch(`/${C300}/models/completion_acts/`, act.id, act);

/*Запрос загруженности (графика) исполнителей - возвращает список сотрудников с информацией о назначенных заявках, требуемый период указывается в params*/
export const GetEmployeesWorkSchedule = (params: IParams) =>
  API.get(`/${REQUEST}/request/schedule/`, undefined, params);

/*Получение организаций, которые могут выполнять заявки по дому*/
export const GetHouseProviders = (
  houseId: string
): Promise<{ results: { id: string; str_name: string }[] }> =>
  API.get(`/${C300}/requests/house_providers/`, houseId);

/* Получение списка позиций каталога */
export const GetAutoDispatchCategories = () =>
  API.get(`/${REQUEST}/request/auto_dispatch/`);

/* Создание объекта авторазбора заявок */
export const addCategoryItem = (data: IParams) =>
  API.gatewayPost(`/${REQUEST}/request/auto_dispatch/`, data);

/* Изменение позиции каталога авторазбора заявок */
export const UpdateRequestsAutoDispatch = (
  id: string,
  data: Partial<CategoryItemType>
) => API.gatewayPatch(`/${REQUEST}/request/auto_dispatch/`, id, data);

/* Удаление  категории авторазбора заявок */
export const deleteCategoryItem = (id: string) =>
  API.delete(`/${REQUEST}/request/auto_dispatch/`, id);

/* Получение записей звонков, привязанных к заявке по call_ids */
export const GetBoundCallsByIds = (call_ids: string[]) =>
  API.gatewayPost(`/${C300}/requests/bound_calls/by_ids/`, { call_ids });

export const CreateRequestPhoneDirectoryProvider = (params: IParams) =>
  API.gatewayPost(`/${C300}/form/request_phone_directories/`, params);

export const PatchRequestPhoneDirectoryProvider = (
  id: string,
  params: IParams
) => API.gatewayPatch(`/${C300}/form/request_phone_directories/`, id, params);

export const GetRequestPhoneDirectoryProviders = (
  params?: IParams,
  id?: string
) => API.get(`/${C300}/form/request_phone_directories/`, id, params);

export const DeleteRequestPhoneDirectoryProvider = (id: string) =>
  API.delete(`/${C300}/form/request_phone_directories/`, id);

export const PatchProvidersPriority = (params: IParams) =>
  API.gatewayPatch(
    `/${C300}/form/request_phone_directories/change_priorities/`,
    undefined,
    params
  );

export const PostShutdownNotification = (data: IShutdownNotificationParams) =>
  API.gatewayPost(`/${C300}/notifications/shutdowns/send/`, data);

// Поиск сторонних лиц по номеру телефона
export const GetOthersCardsByPhone = (params?: IParams) =>
  API.get(`/${C300}/models/third_party_account/`, undefined, params);

// Получение списка сторонних лиц
export const GetOtherPersonAccounts = (params: IParams) =>
  API.get(`/${REQUEST}/others/person/`, undefined, params);

// Получение списка сторонних представителей организации
export const GetOtherEmployeeAccounts = (params: IParams) =>
  API.get(`/${REQUEST}/others/employee/`, undefined, params);

/* Получение сторонних организаций и сотрудников по номеру телефона.
Возвращаются все организации, сотрудники — только с указанным номером. */
export const GetOtherProvidersByPhone = (params?: IParams) =>
  API.get(`/${REQUEST}/others/provider/`, undefined, params);

export const PostCreateNewOther = (params: IParams) => {
  return API.gatewayPost(
    `/${REQUEST}/api/v4/models/third_party_account/`,
    params
  );
};

// Создание сторонней организации
export const CreateNewOtherProvider = (params: IParams) =>
  API.gatewayPost(`/${REQUEST}/others/provider/`, params);

// Создание представителя сторонней организации
export const CreateNewOtherEmployee = (params: IParams) =>
  API.gatewayPost(`/${REQUEST}/others/employee/`, params);

// Создание стороннего лица
export const CreateNewOtherPerson = (params: IParams) =>
  API.gatewayPost(`/${REQUEST}/others/person/`, params);

export const PatchUpdateOther = (id: string, params: IParams) =>
  API.gatewayPatch(`/${C300}/models/third_party_account/`, id, params);

/*
   Получение средней оценки заявок пользователем, а также количества
   оцененных заявок
 */
// TODO неверный эндпоинт, на бэке по данному функционалу рудимент, будут перерабатывать 
export const GetTenantRequestRating = (tenantId: string) =>
  API.get(`/${REQUEST}/request/rate/tenant/`, tenantId);

// Получение загруженного изображения
export const GetRequestImage = (url: string) => API.gatewayUnparsedGet(url);

// Преобразование заявки в обращение
export const CreateAppealFromRequest = (
  requestId: string,
  data: IAppealFromRequest
) =>
  API.gatewayPost(
    `/${REQUEST}/request/employee/convert/to_appeal/${requestId}/`,
    data
  );

/* Логировать переход из Dispatcher в C-300 */
export const LogRedirectToC300 = () =>
  API.gatewayPost('/requests/logs/redirect_to_c300/');

// Справочник алгоритмов
// Получение данных о подрядных организациях. Поиск подрядчиков осуществляется по всем домам организации.
export const GetAlgoritmsDirectoryContractors = () =>
  API.get(`/${C300}/models/house_contractors/`);

// Получение данных о конкретном подрядчике (в том числе списка айди доступных ему домов, которые отданы ему на подряд УО)
export const GetAlgoritmsDirectoryContractorById = (params: IParams) =>
  API.get(`/${C300}/models/house_contractors/`, undefined, params);

// Получение списка исполнителей в Справочнике алгоритмов
export const GetAlgoritmsDirectoryExecutors = () =>
  API.get(`/${REQUEST}/request/algorithm/`);

// Создание исполнителя в Справочнике алгоритмов
export const CreateAlgoritmsDirectoryExecutor = (params: IParams) =>
  API.gatewayPost(`/${REQUEST}/request/algorithm/contractor/`, params);

export const SaveAlgoritmsDirectoryExecutor = (id: string, params: IParams) =>
  API.gatewayPatch(`/${REQUEST}/request/algorithm/contractor/`, id, params);

export const DeleteAlgoritmsDirectoryExecutor = (id: string) =>
  API.delete(`/${REQUEST}/request/algorithm/contractor/`, id);

export const CreateAlgorithm = (params: IParams) =>
  API.gatewayPost(`/${REQUEST}/request/algorithm/`, params);

export const SaveAlgorithm = (params: IParams) =>
  API.gatewayPatch(`/${REQUEST}/request/algorithm/`, params.id, params);

export const DeleteAlgorithm = (id: string) =>
  API.delete(`/${REQUEST}/request/algorithm/`, id);

export const UpdateExecutorsOrder = (params: IParams) =>
  API.gatewayPatch(
    `/${REQUEST}/request/algorithm/contractor/positions_executors/`,
    undefined,
    params
  );

export const GetAlgorithmHint = () =>
  API.get(`/${REQUEST}/request/algorithm/hints/`);

export const PostAlgorithmHint = () =>
  API.gatewayPost(`/${REQUEST}/request/algorithm/hints/`);

/* Создать публичную ссылку */
export const CreatePublicLink = (requestId: string) =>
  API.gatewayPost(`/${REQUEST}/request/employee/public_request/`, {
    request_id: requestId,
  });

/* Отправить публичную ссылку */
export const PostPublicLink = (requestId: string, channel: SendingChannel) =>
  API.gatewayPost(
    `/${REQUEST}/request/employee/public_request/share/${channel}`,
    {
      request_id: requestId,
    }
  );

/** Отправить запрос на подключения ИИ-редактора */
export const CreateAiTicket = () =>
  API.gatewayPost(`/${C300}/models/requests/ai/redactor_ticket/`);

/** Проверить статус сообщения на подключение ИИ-редактора */
export const CheckAiTicketStatus = (): Promise<{
  alert: string;
  text: string;
}> => API.get(`/${C300}/models/requests/ai/status/`);

/** Сгенерировать текст по стилю, или исправить опечатки */
export const GenerateAiAnswer = (params: IParams) =>
  API.gatewayPost(`/${AI}/ml/llm/request-text-editor`, params);

/** Автогенерация текста «Выполненные работы» по описанию заявки */
export const AutoGenerateAiAnswer = (params: {
  text: string;
}): Promise<{ answer: string }> =>
  API.gatewayPost(`/${AI}/ml/llm/request-autogen`, params);
